// src/components/analytics/ChartCard.jsx
import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  FileDownload as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Card } from '../../../design-system';

/**
 * ChartCard component for wrapping analytics charts
 */
const ChartCard = ({
  title,
  subtitle = null,
  tooltip = '',
  icon = null,
  color = 'primary',
  magical = false,
  level = 1,
  isLoading = false,
  height = 400,
  actions = [],
  onRefresh = null,
  onDownload = null,
  onExpand = null,
  children,
}) => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAction = (action) => {
    if (action.onClick) {
      action.onClick();
    }
    handleMenuClose();
  };

  return (
    <Card
      magical={magical}
      level={level}
      sx={{ height: '100%' }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        {/* Title with tooltip */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexGrow: 1,
        }}>
          {icon && (
            <Box 
              sx={{ 
                mr: 1.5,
                color: theme.palette[color].main,
                display: 'flex',
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {title}
              {tooltip && (
                <Tooltip title={tooltip}>
                  <InfoIcon 
                    fontSize="small" 
                    sx={{ 
                      ml: 0.5, 
                      color: alpha(theme.palette.text.secondary, 0.5),
                      fontSize: 16,
                    }} 
                  />
                </Tooltip>
              )}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Chart actions */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {onRefresh && (
            <IconButton size="small" onClick={onRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          {onDownload && (
            <IconButton size="small" onClick={onDownload}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
          {onExpand && (
            <IconButton size="small" onClick={onExpand}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          )}
          {actions.length > 0 && (
            <>
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                {actions.map((action, index) => (
                  <MenuItem 
                    key={index} 
                    onClick={() => handleAction(action)}
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Chart content */}
      <Box 
        sx={{ 
          p: 0,
          pt: 0,
          pb: 2,
          px: 2,
          height: isLoading ? height : 'auto',
          position: 'relative',
          minHeight: height,
        }}
      >
        {isLoading ? (
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          children
        )}
      </Box>
    </Card>
  );
};

export default ChartCard;